import {
  Dispatch,
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { IRbac } from '../apis/__shared/rbac.interfaces';
import { useRbacHook } from '../hooks/useRbacHook';
import { localStorageGet } from '../utils/localStorage';
import isAppWithMock from '../utils/isAppWithMock';

type TRbacState = {
  errorMessage?: string;
  isLoading: boolean;
  rbac: IRbac | null;
};

export enum ACTION_TYPE {
  SET_RBAC = 'SET_RBAC',
  FETCHING_DATA = 'FETCHING_DATA',
  FETCH_DATA_FAILURE = 'FETCH_DATA_FAILURE',
}

type Action =
  | { type: ACTION_TYPE.SET_RBAC; payload: IRbac }
  | { type: ACTION_TYPE.FETCHING_DATA; payload: boolean }
  | { type: ACTION_TYPE.FETCH_DATA_FAILURE; payload: string };

const initialState: TRbacState = {
  rbac: null,
  errorMessage: undefined,
  isLoading: true,
};

const RbacContext = createContext<TRbacState | undefined>(undefined);
const RbacDispatchContext = createContext<Dispatch<Action> | undefined>(
  undefined
);

const rbacReducer = (state: TRbacState, action: Action): TRbacState => {
  switch (action.type) {
    case ACTION_TYPE.SET_RBAC:
      return {
        ...state,
        rbac: action.payload,
        errorMessage: undefined,
        isLoading: false,
      };
    case ACTION_TYPE.FETCHING_DATA:
      return { ...state, isLoading: action.payload, errorMessage: '' };
    case ACTION_TYPE.FETCH_DATA_FAILURE:
      return { ...state, isLoading: false, errorMessage: action.payload };
    default:
      throw new Error('Unhandled action type');
  }
};

export const RbacProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(rbacReducer, initialState);

  const {
    data: rbacData,
    isFetching,
    fetchStatus,
    error,
    isError,
  } = useRbacHook();
  const hasToken = Boolean(localStorageGet('token'));
  const hasRbac = Boolean(rbacData ?? state.rbac);
  const isRbacLoading = hasToken && !hasRbac && !error && fetchStatus === 'fetching';
  const isIdleWithoutRbac = hasToken && !hasRbac && !error && fetchStatus === 'idle';

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('[RBAC] state', {
        hasToken,
        hasRbac,
        fetchStatus,
        isFetching,
        isError,
        hasError: Boolean(error),
        isRbacLoading,
      });
    }
  }, [
    hasToken,
    hasRbac,
    fetchStatus,
    isFetching,
    isError,
    error,
    isRbacLoading,
  ]);

  useEffect(() => {
    dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: isRbacLoading });
  }, [isRbacLoading]);

  useEffect(() => {
    if (error) {
      dispatch({
        type: ACTION_TYPE.FETCH_DATA_FAILURE,
        payload: error.response?.data?.message || 'Failed to fetch RBAC data',
      });
    }
  }, [error]);

  useEffect(() => {
    if (isAppWithMock()) return;
    if (rbacData) {
      dispatch({
        type: ACTION_TYPE.SET_RBAC,
        payload: rbacData,
      });
    }
  }, [rbacData]);

  const value = useMemo(() => {
    // With mock role switcher, reducer updates must win over stale query data.
    const rbac: IRbac | null =
      (isAppWithMock()
        ? state.rbac ?? rbacData
        : rbacData ?? state.rbac) ?? null;
    const fromReducer =
      hasToken && state.errorMessage && state.errorMessage !== ''
        ? state.errorMessage
        : undefined;
    const fromQuery =
      hasToken && isError && !rbac && error && !isFetching
        ? error.response?.data?.message || 'Failed to fetch RBAC data'
        : undefined;
    const fromIdleState = isIdleWithoutRbac
      ? 'Failed to initialize RBAC permissions'
      : undefined;
    const errorMessage = fromReducer ?? fromQuery ?? fromIdleState;

    return {
      ...state,
      rbac,
      isLoading: isRbacLoading,
      errorMessage,
    };
  }, [
    state,
    isRbacLoading,
    rbacData,
    hasToken,
    isError,
    error,
    isFetching,
    isIdleWithoutRbac,
  ]);

  return (
    <RbacContext.Provider value={value}>
      <RbacDispatchContext.Provider value={dispatch}>
        {children}
      </RbacDispatchContext.Provider>
    </RbacContext.Provider>
  );
};

export const useRbacState = () => {
  const context = useContext(RbacContext);
  if (context === undefined) {
    throw new Error('useRbacState must be used within a RbacProvider');
  }
  return context;
};

export const useRbacActions = () => {
  const dispatch = useContext(RbacDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useRbacActions must be used within a RbacProvider');
  }

  return { dispatch };
};
